'use client'

import cn from 'classnames'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useEffect, useRef, useState } from 'react'

import Logo from '@/public/logo.svg'

import { Button } from './Button'

type T = {}

export const Header = ({}: T) => {
  const [isMenu, setMenu] = useState(false)
  const [isScroll, setIsScroll] = useState(false)
  const headerRef = useRef<HTMLElement>(null)

  const pathname = usePathname()
  useEffect(() => {
    setMenu(false)
    setIsScroll(false)
  }, [pathname])

  useEffect(() => {
    const targetElement = document.querySelector('[data-id="HeaderKey"]')
    const headerElement = headerRef.current

    if (!targetElement || !headerElement) return

    // ヘッダーの高さを動的に取得
    const headerHeight = headerElement.offsetHeight

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // 要素が画面から出た時（跨いだ時）にisScrollをtrueに
          setIsScroll(!entry.isIntersecting)
        })
      },
      {
        // ヘッダーの高さ分だけ上にマージンを設定（ヘッダーの高さを除外）
        rootMargin: `-${headerHeight}px 0px 0px 0px`,
        threshold: 0,
      },
    )

    observer.observe(targetElement)

    return () => {
      observer.disconnect()
    }
  }, [pathname])

  // 画面の横幅変更時にリセット
  useEffect(() => {
    let previousWidth = window.innerWidth

    const handleResize = () => {
      const currentWidth = window.innerWidth
      // 横幅が変更された場合のみリセット
      if (currentWidth !== previousWidth) {
        setIsScroll(false)
        previousWidth = currentWidth
      }
    }

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  return (
    <>
      <header
        ref={headerRef}
        className={cn(
          'sticky top-0 z-40 flex w-full items-center justify-between bg-white px-lg py-6 transition-colors border-b duration-100',
          {
            'border-transparent': !isScroll,
            'border-body-10': isScroll,
          },
        )}
      >
        <Link href="/" className="relative z-40">
          <Logo className="w-48 lg:w-60" />
        </Link>
        <div
          className="relative z-40 h-4 w-6 cursor-pointer lg:hidden"
          onClick={() => setMenu(!isMenu)}
        >
          <div
            className={cn('absolute top-0 h-0.5 w-full bg-body transition', {
              'rotate-[45deg] translate-y-[7px]': isMenu,
            })}
          ></div>
          <div
            className={cn(
              'absolute top-1/2 -translate-y-1/2 h-0.5 w-full bg-body transition',
              {
                'opacity-0': isMenu,
              },
            )}
          ></div>
          <div
            className={cn('absolute bottom-0 h-0.5 w-full bg-body transition', {
              'rotate-[-45deg] -translate-y-[7px]': isMenu,
            })}
          ></div>
        </div>
        <nav
          className={cn(
            'fixed top-0 left-0 w-full h-full bg-white gap-x-8 items-center text-base lg:text-sm font-medium flex lg:opacity-100 transition-opacity z-30 flex-col lg:flex-row justify-center text-center  lg:justify-end lg:static lg:pointer-events-auto gap-y-8',
            {
              'opacity-100': isMenu,
              'opacity-0 pointer-events-none': !isMenu,
            },
          )}
        >
          <ul className="flex flex-col gap-x-2 gap-y-6 lg:flex-row">
            {[
              { text: '堀口会計について', href: '/about' },
              { text: '確定申告の税務顧問', href: '/service/tax-return' },
              { text: '企業の税務顧問', href: '/service/tax-advisor' },
            ].map((a) => (
              <li key={a.text} className="">
                <Link href={a.href} className="group relative p-2">
                  {a.text}
                  <div
                    className={cn(
                      'absolute -left-1.5 top-1/2 -translate-y-1/2 lg:top-auto lg:-bottom-1 lg:translate-y-0 lg:left-1/2 size-1.5 lg:-translate-x-1/2 transition-colors',
                      {
                        'bg-primary': pathname === a.href,
                        'bg-transparent': pathname !== a.href,
                      },
                    )}
                  ></div>
                </Link>
              </li>
            ))}
          </ul>
          <div>
            <Button href="/contact" text="お問合せ" />
          </div>
        </nav>
      </header>
    </>
  )
}
